import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  Users as UsersIcon,
  Flag,
  ShieldCheck,
  ShieldAlert,
  ScrollText,
  Sparkles,
  UsersRound,
  Award,
  MonitorPlay,
  HeartHandshake,
  LogOut,
  Menu,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/mypersonalworkplace", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/mypersonalworkplace/users", label: "Users", icon: UsersIcon, id: "users" },
  { to: "/mypersonalworkplace/matchmaker", label: "Matchmaker", icon: HeartHandshake, id: "matchmaker" },
  { to: "/mypersonalworkplace/communities", label: "Communities", icon: UsersRound, id: "communities" },
  { to: "/mypersonalworkplace/vouches", label: "Vouches", icon: Award, id: "vouches" },
  { to: "/mypersonalworkplace/watch", label: "Watch Parties", icon: MonitorPlay, id: "watch" },
  { to: "/mypersonalworkplace/reports", label: "Reports", icon: Flag, id: "reports" },
  { to: "/mypersonalworkplace/verifications", label: "Verifications", icon: ShieldCheck, id: "verifications" },
  { to: "/mypersonalworkplace/safety", label: "Trust & Safety", icon: ShieldAlert, id: "safety" },
  { to: "/mypersonalworkplace/audit", label: "Audit Log", icon: ScrollText, id: "audit" },
  { to: "/mypersonalworkplace/demo", label: "Demo Profiles", icon: Sparkles, id: "demo" },
];

function SidebarContent({ user, onLogout, onNavigate }) {
  return (
    <div className="flex flex-col h-full bg-[hsl(189_82%_10%)]">
      <div className="h-16 flex items-center gap-2.5 px-6 border-b border-border/60">
        <Logo size={36} />
        <div>
          <div className="font-display text-lg leading-none tracking-tight">Amoreska</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">Admin Console</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-5 space-y-1" data-testid="sidebar-nav">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            data-testid={`nav-${item.id}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
                isActive
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/70"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-border/60">
        <div className="px-3 py-2 mb-2">
          <div className="text-sm font-semibold truncate">{user?.name || "Admin"}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors"
        >
          <LogOut className="h-[18px] w-[18px]" />
          Sign out
        </button>
      </div>
    </div>
  );
}

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border/70 flex-col fixed inset-y-0 left-0 z-20">
        <SidebarContent user={user} onLogout={logout} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 border-border/70">
          <SidebarContent user={user} onLogout={logout} onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-10 h-14 flex items-center gap-3 px-4 border-b border-border/70 bg-[hsl(189_82%_10%)]">
        <button
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-btn"
          className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary/70"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          <Logo size={28} rounded="rounded-lg" />
          <span className="font-display text-lg">Amoreska</span>
        </div>
      </div>

      {/* Main */}
      <main className="lg:ml-64 min-w-0">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">{children}</div>
      </main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-7 flex-wrap">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 text-sm">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
